import { FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";

import useTypedContext from "hooks/useTypedContext";
import useUpdateStack from "shared/Stack/useUpdateStack";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import { stringIsRequired } from "utils/formValidators";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import Textarea from "ds/components/Textarea";
import FormFieldTagsMagicBanner from "components/FormFields/Tags/MagicLabels";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useStackMoveSpaces from "views/Stack/useStackMoveSpaces";
import FormFooterEdit from "components/Forms/FooterEdit";

import { getStackDetailsNameTooltip } from "../components/getTooltips";
import { REFETCH_STACK_AND_STACK_SETTINGS_QUERIES } from "../constants";
import { getFormDefaultValues, mapCreateStackDetailsUpdateInput } from "./helpers";
import { StackSettingsContextData } from "../Context";

export type StackSettingsDetailsFormFields = {
  name: string;
  space: string;
  labels: Array<{ value: string }>;
  description: string;
};

const StackSettingsDetailsEdit = () => {
  const { stackSettings } = useTypedContext(StackSettingsContextData);

  const { stackUpdate, loading } = useUpdateStack({
    stack: stackSettings,
    refetchQueries: REFETCH_STACK_AND_STACK_SETTINGS_QUERIES,
  });

  const stackDetailsForm = useForm<StackSettingsDetailsFormFields>({
    defaultValues: getFormDefaultValues(stackSettings),
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
  } = stackDetailsForm;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const onSubmit = (formData: StackSettingsDetailsFormFields) => {
    stackUpdate(mapCreateStackDetailsUpdateInput(formData));
  };

  useEffect(() => {
    reset(getFormDefaultValues(stackSettings));
  }, [reset, stackSettings]);

  const { canMoveStack, spaces } = useStackMoveSpaces(stackSettings);

  return (
    <FormProvider {...stackDetailsForm}>
      <FormField
        noMargin
        label="Name"
        error={errors?.name?.message}
        {...getStackDetailsNameTooltip()}
      >
        {({ ariaInputProps }) => (
          <Input
            placeholder="Name your stack"
            {...register("name", {
              validate: stringIsRequired(),
              setValueAs: (value: string) => value.trim(),
            })}
            error={!!errors?.name?.message}
            {...ariaInputProps}
          />
        )}
      </FormField>
      <FormFieldSpace noMargin spaces={spaces} disabled={!canMoveStack} />
      {!canMoveStack && (
        <Banner variant="info" title="Changing space is disabled">
          You don't have permission to move the stack to another space.
        </Banner>
      )}
      <Box direction="column" fullWidth>
        <FormFieldTags noMargin label="Labels" tagName="label" name="labels" isOptional />
      </Box>
      <FormFieldTagsMagicBanner entityType="stack" />
      <FormField noMargin label="Description" isOptional>
        {({ ariaInputProps }) => (
          <Textarea
            placeholder="Enter description here..."
            maxRows={15}
            {...register("description")}
            {...ariaInputProps}
          />
        )}
      </FormField>
      <FormFooterEdit
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => reset(getFormDefaultValues(stackSettings))}
        isDirty={isDirty}
        submitLoading={loading}
      />
    </FormProvider>
  );
};

export default StackSettingsDetailsEdit;
